import type { LucideIcon } from 'lucide-react'
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'

import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
    CircleAlert,
    CircleCheck,
    Eye,
    EyeOff,
    Lock
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { passwordStrength } from '@/lib/validation'

const inputBase = 'h-12 w-full rounded-2xl bg-white text-sm text-foreground ring-1 ring-border outline-none transition-shadow placeholder:text-muted-foreground/70 focus:ring-2 focus:ring-primary/40 disabled:opacity-60'

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
    id: string
    label: string
    icon?: LucideIcon
    error?: string | null
    hint?: ReactNode
    ltr?: boolean
}

export const Field = ({ id, label, icon: Icon, error, hint, ltr = false, className, ...props }: FieldProps): ReactNode => {
    const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined

    return (
        <div className='flex flex-col gap-1.5'>
            <label htmlFor={id} className='text-sm font-semibold text-foreground'>
                {label}
            </label>

            <div className='relative'>
                {Icon && <Icon className='pointer-events-none absolute top-1/2 right-4 size-[18px] -translate-y-1/2 text-muted-foreground' aria-hidden='true' />}
                <input
                    id={id}
                    dir={ltr ? 'ltr' : undefined}
                    aria-invalid={Boolean(error)}
                    aria-describedby={describedBy}
                    className={cn(
                        inputBase,
                        Icon ? 'pr-11' : 'pr-4',
                        'pl-4',
                        ltr && 'text-left placeholder:text-right',
                        error && 'ring-destructive/60 focus:ring-destructive/40',
                        className
                    )}
                    {...props}
                />
            </div>

            <FieldMessage id={id} error={error} hint={hint} />
        </div>
    )
}

interface FieldMessageProps {
    id: string
    error?: string | null
    hint?: ReactNode
}

const FieldMessage = ({ id, error, hint }: FieldMessageProps): ReactNode => {
    return (
        <AnimatePresence mode='wait' initial={false}>
            {error ? (
                <motion.p
                    key='error'
                    id={`${id}-error`}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className='flex items-center gap-1.5 text-xs text-destructive'
                >
                    <CircleAlert className='size-3.5 shrink-0' aria-hidden='true' />
                    {error}
                </motion.p>
            ) : hint ? (
                <motion.div key='hint' id={`${id}-hint`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className='text-xs text-muted-foreground'>
                    {hint}
                </motion.div>
            ) : null}
        </AnimatePresence>
    )
}

interface PasswordFieldProps extends Omit<FieldProps, 'icon' | 'ltr' | 'type'> {
    showStrength?: boolean
}

export const PasswordField = ({ id, label, error, hint, showStrength = false, value, className, ...props }: PasswordFieldProps): ReactNode => {
    const [visible, setVisible] = useState(false)
    const strength = passwordStrength(String(value ?? ''))
    const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined

    return (
        <div className='flex flex-col gap-1.5'>
            <label htmlFor={id} className='text-sm font-semibold text-foreground'>
                {label}
            </label>

            <div className='relative'>
                <Lock className='pointer-events-none absolute top-1/2 right-4 size-[18px] -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                <input
                    id={id}
                    type={visible ? 'text' : 'password'}
                    dir='ltr'
                    value={value}
                    aria-invalid={Boolean(error)}
                    aria-describedby={describedBy}
                    className={cn(
                        inputBase,
                        'pr-11 pl-12 text-left placeholder:text-right',
                        error && 'ring-destructive/60 focus:ring-destructive/40',
                        className
                    )}
                    {...props}
                />
                <button
                    type='button'
                    onClick={() => setVisible((current) => !current)}
                    aria-label={visible ? "پنهان کردن رمز عبور" : "نمایش رمز عبور"}
                    aria-pressed={visible}
                    className='absolute top-1/2 left-2 grid size-9 -translate-y-1/2 place-items-center rounded-xl text-muted-foreground transition hover:bg-muted hover:text-foreground'
                >
                    {visible ? <EyeOff className='size-[18px]' aria-hidden='true' /> : <Eye className='size-[18px]' aria-hidden='true' />}
                </button>
            </div>

            {showStrength && String(value ?? '').length > 0 && (
                <div className='flex items-center gap-2' aria-live='polite'>
                    <div className='flex flex-1 gap-1' aria-hidden='true'>
                        {[1, 2, 3, 4].map((step) => (
                            <span
                                key={step}
                                className={cn(
                                    'h-1.5 flex-1 rounded-full transition-colors duration-300',
                                    strength.score >= step
                                        ? strength.score <= 1 ? 'bg-destructive' : strength.score === 2 ? 'bg-warning' : 'bg-success'
                                        : 'bg-border'
                                )}
                            />
                        ))}
                    </div>
                    <span className='w-16 shrink-0 text-left text-xs font-semibold text-muted-foreground'>
                        {strength.label}
                    </span>
                </div>
            )}

            <FieldMessage id={id} error={error} hint={hint} />
        </div>
    )
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    id: string
    label: string
    error?: string | null
    hint?: ReactNode
}

export const TextAreaField = ({ id, label, error, hint, className, ...props }: TextAreaFieldProps): ReactNode => {
    const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined

    return (
        <div className='flex flex-col gap-1.5'>
            <label htmlFor={id} className='text-sm font-semibold text-foreground'>
                {label}
            </label>
            <textarea
                id={id}
                aria-invalid={Boolean(error)}
                aria-describedby={describedBy}
                className={cn(
                    'min-h-32 w-full resize-y rounded-2xl bg-white px-4 py-3 text-sm leading-7 text-foreground ring-1 ring-border outline-none transition-shadow placeholder:text-muted-foreground/70 focus:ring-2 focus:ring-primary/40',
                    error && 'ring-destructive/60 focus:ring-destructive/40',
                    className
                )}
                {...props}
            />
            <FieldMessage id={id} error={error} hint={hint} />
        </div>
    )
}

interface FormAlertProps {
    tone?: 'error' | 'success'
    children: ReactNode
}

export const FormAlert = ({ tone = 'error', children }: FormAlertProps): ReactNode => {
    const Icon = tone === 'error' ? CircleAlert : CircleCheck

    return (
        <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            role={tone === 'error' ? 'alert' : 'status'}
            className={cn(
                'flex items-start gap-2.5 rounded-2xl p-3.5 text-sm',
                tone === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'
            )}
        >
            <Icon className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
            <div>{children}</div>
        </motion.div>
    )
}
